/**
 * A crash inside one screen stays inside that screen.
 *
 * The boundary in `main.tsx` is the last line: it replaces the whole client
 * with the crash page, and a bad room address is not worth that — the shell,
 * the sidebar and every other room are fine. This one wraps a single route,
 * shows the failure in place, and lets go of it as soon as the address
 * changes, so navigating away is enough to recover.
 */

import { Component } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Button, EmptyState } from '@/components/ui'
import { recordDiagnostic } from '@/lib/diagnostics'

interface RouteErrorBoundaryProps {
  /** The screen forgets its error when this changes — typically the pathname. */
  resetKey: string
  children: ReactNode
}

interface RouteErrorBoundaryState {
  error: Error | null
}

export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  override state: RouteErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: unknown): RouteErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }

  override componentDidCatch(): void {
    recordDiagnostic('render_error')
    console.error('relaii: a screen failed to draw (details omitted to protect message content)')
  }

  override componentDidUpdate(previous: RouteErrorBoundaryProps): void {
    if (previous.resetKey !== this.props.resetKey && this.state.error !== null) {
      this.setState({ error: null })
    }
  }

  private readonly retry = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children

    return (
      <EmptyState
        fill
        title="This screen could not be shown"
        description={
          error.message === '' ? 'Something went wrong while drawing it.' : error.message
        }
        action={
          <>
            <Button onClick={this.retry}>Try again</Button>
            <Link to="/overview">Back to your rooms</Link>
          </>
        }
      />
    )
  }
}
